import { useDelete } from '@refinedev/core'
import { useDataGrid, List } from '@refinedev/mui'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Box, Chip, IconButton, MenuItem, TextField, Typography } from '@mui/material'
import ArchiveIcon from '@mui/icons-material/Archive'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useLocation, useNavigate } from 'react-router-dom'
import type { MouseEvent } from 'react'

const CATEGORY_OPTIONS = ['', 'tone', 'terminology', 'fact', 'preference', 'correction']
const STATUS_OPTIONS = ['', 'active', 'superseded', 'archived']
const SCOPE_OPTIONS = ['', 'org', 'need']

export const MemoriesList: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { mutate: archiveMemory } = useDelete()

  const params = new URLSearchParams(location.search)
  const orgSearch = params.get('org') || ''
  const scope = params.get('scope') || ''
  const category = params.get('category') || ''
  const status = params.get('status') || ''

  const filters = [] as any[]
  if (orgSearch) {
    filters.push({ field: 'org_name', operator: 'contains', value: orgSearch })
  }
  if (scope) {
    filters.push({ field: 'scope', operator: 'eq', value: scope })
  }
  if (category) {
    filters.push({ field: 'category', operator: 'eq', value: category })
  }
  if (status) {
    filters.push({ field: 'status', operator: 'eq', value: status })
  }

  const { dataGridProps } = useDataGrid({
    resource: 'memories',
    filters: {
      permanent: filters,
    },
    sorters: {
      initial: [{ field: 'created_at', order: 'desc' }],
    },
  })

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(location.search)
    if (!value) {
      next.delete(key)
    } else {
      next.set(key, value)
    }
    navigate({ pathname: location.pathname, search: next.toString() })
  }

  const handleArchive = (event: MouseEvent, row: any) => {
    event.stopPropagation()

    if (row.status === 'archived') {
      return
    }

    const confirmed = window.confirm('Archive this memory?')
    if (!confirmed) {
      return
    }

    archiveMemory(
      { resource: 'memories', id: row.id }
    )
  }

  const columns: GridColDef[] = [
    {
      field: 'org_name',
      headerName: 'Org',
      minWidth: 220,
      flex: 1,
      valueGetter: (params) => params.row.org_name || params.row.org_uuid,
    },
    {
      field: 'scope',
      headerName: 'Scope',
      minWidth: 90,
      renderCell: ({ row }) => <Chip size="small" label={row.scope} />,
    },
    {
      field: 'need_name',
      headerName: 'Need',
      minWidth: 180,
      valueGetter: (params) => params.row.need_name || (params.row.need_id ? `#${params.row.need_id}` : ''),
    },
    {
      field: 'category',
      headerName: 'Category',
      minWidth: 130,
      renderCell: ({ row }) => <Chip size="small" variant="outlined" label={row.category} />,
    },
    {
      field: 'content',
      headerName: 'Content',
      minWidth: 320,
      flex: 2,
      renderCell: ({ value }) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 420 }}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'confidence',
      headerName: 'Confidence',
      minWidth: 100,
      valueFormatter: ({ value }) => Number(value || 0).toFixed(2),
    },
    {
      field: 'source_type',
      headerName: 'Source',
      minWidth: 90,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      renderCell: ({ row }) => {
        const color = row.status === 'active' ? 'success' : row.status === 'archived' ? 'default' : 'warning'
        return <Chip size="small" color={color} label={row.status} />
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      minWidth: 180,
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : ''),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/memories/${row.id}`) }}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="warning"
            onClick={(event) => handleArchive(event, row)}
            disabled={row.status === 'archived'}
          >
            <ArchiveIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ]

  return (
    <List>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Org Search"
          size="small"
          value={orgSearch}
          onChange={(event) => updateFilter('org', event.target.value)}
        />
        <TextField
          select
          label="Scope"
          size="small"
          value={scope}
          onChange={(event) => updateFilter('scope', event.target.value)}
          sx={{ minWidth: 120 }}
        >
          {SCOPE_OPTIONS.map((option) => (
            <MenuItem key={option || 'all'} value={option}>
              {option || 'All'}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Category"
          size="small"
          value={category}
          onChange={(event) => updateFilter('category', event.target.value)}
          sx={{ minWidth: 160 }}
        >
          {CATEGORY_OPTIONS.map((option) => (
            <MenuItem key={option || 'all'} value={option}>
              {option || 'All'}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          size="small"
          value={status}
          onChange={(event) => updateFilter('status', event.target.value)}
          sx={{ minWidth: 140 }}
        >
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option || 'all'} value={option}>
              {option || 'All'}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        getRowId={(row) => row.id}
        onRowClick={(params) => navigate(`/memories/${params.row.id}`)}
      />
    </List>
  )
}
